import { useQuery } from "@tanstack/react-query";
import { atomWithStorage } from "jotai/utils";
import { useAtomValue } from "jotai";
import { useMemo } from "react";

import SharedRecordAPI from "../../api/SharedRecordAPI";
import FieldUtils from "../../../utils/FieldUtils";
import { Module } from "../../../types/TenantInterfaces";

interface UseSharedRecord {
  record: any;
  moduleSchema: Module;
  userName: string;
  success: boolean;
  requiresPassword?: boolean;
  viewID: string;
  pageID: string;
}

export const shareRecordPasswordAtom = atomWithStorage<Record<string, string>>(
  "share-record-passwords",
  {},
  undefined,
  { getOnInit: true }
);

// For getting a Shared Record by the shared record id (on /share page)
export default function useSharedRecord(sharedRecordID: string) {
  const pagePasswords = useAtomValue(shareRecordPasswordAtom);

  const password = useMemo(() => {
    if (!sharedRecordID) return null;

    return pagePasswords[`${sharedRecordID}`] || null;
  }, [pagePasswords, sharedRecordID]);

  const sharedRecord = useQuery<UseSharedRecord>({
    queryKey: ["sharedRecord", sharedRecordID],
    enabled: !!sharedRecordID,
    queryFn: () =>
      SharedRecordAPI.getSharedRecord(sharedRecordID, password ?? "").then(
        (data) => {
          if (data?.data?.data?.moduleSchema) {
            FieldUtils.addDefaultsToSchema(
              {
                CRM: { Modules: [data.data.data.moduleSchema] },
              },
              true
            );
          }

          return data?.data?.data;
        }
      ),
  });

  return {
    record: sharedRecord.data?.record,
    moduleSchema: sharedRecord.data?.moduleSchema,
    userName: sharedRecord.data?.userName,
    success: sharedRecord.data?.success,
    requiresPassword: sharedRecord.data?.requiresPassword ?? false,
    viewID: sharedRecord.data?.viewID,
    pageID: sharedRecord.data?.pageID,
    loading: sharedRecord.isLoading,
  };
}
